import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Icon, Label, Utils } from 'mw-style-react';
import { UPDATE_USER_SETTINGS } from 'constants';
import { MIN_PANEL_WIDTH } from '@control-front-end/common/constants/graphActors';
import cn from 'classnames';
import useIntl from 'useIntl';
import { makeGetScriptFile } from '../../../../selectors';
import usePanelResize from './hooks/usePanelResize';
import PagePreview from './components/PagePreview/PagePreview';
import './ScriptFilePreview.scss';
import mes from './intl';

/**
 * Превью страницы в редакторе скрипта
 * @param props
 * @returns {JSX.Element}
 * @constructor
 */
function ScriptFilePreview(props) {
  const t = useIntl();
  const dispatch = useDispatch();
  const resizer = useRef();
  const filePreviewRef = useRef();
  const { accId, scriptRef, stage, pagesFolderId } = props;
  const { scriptPanelPercentage } = useSelector((state) => state.settings);
  const file = useSelector((state) => makeGetScriptFile(state, props));
  const folder = useSelector((state) =>
    makeGetScriptFile(state, { fileId: file.folderId, objType: 'folder' })
  );

  const initPanelSize = () => {
    const fileContainer = filePreviewRef.current.parentElement;
    const { width } = fileContainer.getBoundingClientRect();
    const value = scriptPanelPercentage ? scriptPanelPercentage / 100 : 0.5;
    filePreviewRef.current.style.width = `${width * value}px`;
  };

  useEffect(() => {
    if (filePreviewRef.current) initPanelSize();
  }, []);

  /**
   * Сохранить размер панели превью в настройки
   */
  const savePanelSettings = (percentage) => {
    dispatch({
      type: UPDATE_USER_SETTINGS.REQUEST,
      payload: { accId: null, scriptPanelPercentage: percentage },
    });
  };
  const debouncedSaving = Utils.debounce(savePanelSettings, 2000);

  /**
   * Обработчик изменения размера панели
   */
  const handleResizePreview = (newX) => {
    const { left, width } = filePreviewRef.current.getBoundingClientRect();
    const newWidth = width + left - newX;
    if (newWidth < MIN_PANEL_WIDTH) return;
    filePreviewRef.current.style.width = `${newWidth}px`;
    const fileContainer = filePreviewRef.current.parentElement;
    const { width: containerWidth } = fileContainer.getBoundingClientRect();
    const percentage = Math.round((newWidth / containerWidth) * 100);
    debouncedSaving(percentage);
  };
  usePanelResize({ ref: resizer, callback: handleResizePreview });

  const noPageSelected = () => (
    <div styleName="sfp__empty">
      <Icon type="page" size="xlarge" />
      <Label value={t(mes.noPageSelected)} />
    </div>
  );

  const isPageFile = folder && folder.parentId === pagesFolderId;
  const page = isPageFile ? folder.title : null;
  return (
    <div ref={filePreviewRef} styleName="sfp">
      <div ref={resizer} styleName="sfp__resizer" />
      <div styleName="sfp__header">
        <Icon type="eye" />
        <Label fontWeight="semibold" value="Preview" />
      </div>
      {page ? (
        <div id="cdu" styleName={cn('sfp__content', { hidden: !page })}>
          {page ? (
            <PagePreview
              accId={accId}
              app={scriptRef}
              stage={stage}
              page={page}
            />
          ) : null}
        </div>
      ) : (
        noPageSelected()
      )}
    </div>
  );
}

ScriptFilePreview.propTypes = {
  accId: PropTypes.string,
  scriptRef: PropTypes.string.isRequired,
  pagesFolderId: PropTypes.number,
  stage: PropTypes.string,
};

export default ScriptFilePreview;
